import {Box, Stack, Typography} from '@mui/material';
import {CredentialResponse, GoogleLogin, googleLogout} from '@react-oauth/google';
import {useCallback, useEffect, useRef, useState} from 'react';

import {ClaudeRunsPage, ClaudeRunsPageState, RUNS_PRESET_MS, RunsRange} from './components/ClaudeRunsPage';
import {GrantsPage} from './components/GrantsPage';
import {MyGrantsPage} from './components/MyGrantsPage';
import {NewRequestDialog} from './components/NewRequestDialog';
import {PRESET_MS, Range, RequestsPage, RequestsPageState} from './components/RequestsPage';
import {PageKey, Sidebar, readPageFromHash, writePageToHash} from './components/Sidebar';
import {UsersPage} from './components/UsersPage';
import {
    GrantsResponse,
    Me,
    SessionExpiredError,
    TenantCatalogEntry,
    TwingateUnreachableError,
    UserProfile,
    fetchClaudeRunStats,
    fetchClaudeRuns,
    fetchGrants,
    fetchMe,
    fetchRequests,
    fetchTenants,
    fetchUsers,
    setSessionExpiredHandler,
    syncGrants,
} from './lib/api';
import {RequestPrefill, clearRequestPrefill, readRequestPrefill} from './lib/request-deep-link';

const STORAGE_KEY = 'access-ops-session';

export interface AuthSession {
    idToken: string;
    email: string;
    expiresAt: number;
}

const MOCK_MODE = import.meta.env.VITE_MOCK === '1';

export function App() {
    const [session, setSession] = useState<AuthSession | null>(
        MOCK_MODE ? {idToken: 'mock', email: 'mock@ennabl.com', expiresAt: Date.now() + 86_400_000} : loadSession
    );

    const onLogin = (response: CredentialResponse) => {
        if (!response.credential) {
            return;
        }
        const decoded = decodeJwtPayload(response.credential);
        if (!decoded.email || !decoded.exp) {
            return;
        }
        const next: AuthSession = {
            idToken: response.credential,
            email: decoded.email,
            expiresAt: decoded.exp * 1000,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        setSession(next);
    };

    const onSignOut = () => {
        googleLogout();
        localStorage.removeItem(STORAGE_KEY);
        setSession(null);
    };

    if (!session) {
        return (
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh'}}>
                <Stack spacing={2} alignItems="center">
                    <Typography variant="h5">AccessOps</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Sign in with your Ennabl Google account.
                    </Typography>
                    <GoogleLogin
                        onSuccess={onLogin}
                        onError={() => console.error('Google login failed')}
                        hosted_domain="ennabl.com"
                        useOneTap
                    />
                </Stack>
            </Box>
        );
    }

    return <Shell session={session} onSignOut={onSignOut} />;
}

/**
 * Authenticated shell: sidebar + page area. Owns the single /grants fetch and
 * the /me fetch (current user's role + group, used to route the UI). Page is
 * routed via URL hash so refreshes and deep-links survive.
 */
function Shell({session, onSignOut}: {session: AuthSession; onSignOut: () => void}) {
    const [page, setPage] = useState<PageKey>(() => readPageFromHash());
    const [me, setMe] = useState<Me | undefined>();
    const [meError, setMeError] = useState<string | undefined>();
    const [grants, setGrants] = useState<GrantsResponse | undefined>();
    const [grantsLoading, setGrantsLoading] = useState(false);
    const [grantsError, setGrantsError] = useState<string | undefined>();
    const [twingateDown, setTwingateDown] = useState(false);
    const [newRequestOpen, setNewRequestOpen] = useState(false);
    // Requests state lives here (not in RequestsPage) so it survives a nav
    // away to Grants and back — the user shouldn't re-pay the Logs Insights
    // round-trip just because they switched tabs.
    const [requests, setRequests] = useState<RequestsPageState>(() => {
        const now = Date.now();
        return {
            loading: false,
            twingateDown: false,
            since: now - PRESET_MS['24h'],
            until: now,
            preset: '24h',
        };
    });
    // Users state is lifted here (mirroring grants/requests) so it survives a
    // nav away to another page and back — admins shouldn't pay the DDB scan
    // every time they switch tabs.
    const [users, setUsers] = useState<UserProfile[] | undefined>();
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState<string | undefined>();
    const [usersTwingateDown, setUsersTwingateDown] = useState(false);
    // Claude runs (admin audit log) — lifted here for the same reason as Requests:
    // the window query is the expensive part, don't re-pay it on every tab switch.
    const [claudeRuns, setClaudeRuns] = useState<ClaudeRunsPageState>(() => {
        const now = Date.now();
        return {
            loading: false,
            twingateDown: false,
            since: now - RUNS_PRESET_MS['7d'],
            until: now,
            preset: '7d',
        };
    });
    // Full tenant catalog for the Grant/Request access picker — fetched once and
    // independent of /grants so the picker can offer tenants that have no grant
    // yet. A failure here is non-fatal: the dialog falls back to deriving the
    // tenant list from the grants it already has.
    const [tenantCatalog, setTenantCatalog] = useState<TenantCatalogEntry[]>([]);

    useEffect(() => {
        const onHashChange = () => setPage(readPageFromHash());
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    /**
     * Arriving from another app's deep link (`#/requests?kind=imp-ai&subject=…`):
     * open the dialog pre-filled instead of dropping the operator on an empty
     * page. Today's sender is ennabl-ai's admin UI when its impersonation gate
     * refuses.
     *
     * The prefill is consumed once and stripped from the hash, so a reload does
     * not silently re-apply a stale subject.
     */
    const [requestPrefill, setRequestPrefill] = useState<RequestPrefill | undefined>();
    useEffect(() => {
        const prefill = readRequestPrefill();
        if (!prefill) {
            return;
        }
        setRequestPrefill(prefill);
        setNewRequestOpen(true);
        clearRequestPrefill();
    }, []);

    // Any 401 from the API means the Google id token has expired. Clear the
    // session so the GoogleLogin button (with `useOneTap`) gets a chance to
    // re-auth silently against the still-live Google session in the browser.
    useEffect(() => {
        setSessionExpiredHandler(onSignOut);
        return () => setSessionExpiredHandler(null);
    }, [onSignOut]);

    // Fetch the current user's role + group once after login. Errors here are
    // soft — the UI falls back to "user/needs-approval" behavior, which is the
    // same default the backend would apply if there's no DDB row yet.
    useEffect(() => {
        let cancelled = false;
        fetchMe(session.idToken)
            .then(m => {
                if (!cancelled) {
                    setMe(m);
                }
            })
            .catch(err => {
                if (!cancelled) {
                    setMeError(err instanceof Error ? err.message : String(err));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [session.idToken]);

    const navigate = useCallback((key: PageKey) => {
        writePageToHash(key);
        setPage(key);
    }, []);

    // Reload semantics:
    //   - `loadGrants()` — pure read of the DDB snapshot. Cheap, runs on mount.
    //   - `reloadGrants()` — admin-triggered: POST /grants/sync to refresh the
    //     snapshot from the live sources, then read. Slow (~10s) but accurate.
    // Non-admins can't sync (the route is 403 for them) so their Reload button
    // just re-reads the snapshot.
    const loadGrants = useCallback(async () => {
        setGrantsLoading(true);
        setGrantsError(undefined);
        setTwingateDown(false);
        try {
            const response = await fetchGrants(session.idToken);
            setGrants(response);
        } catch (err) {
            if (err instanceof SessionExpiredError) {
                return;
            }
            if (err instanceof TwingateUnreachableError) {
                setTwingateDown(true);
            } else {
                setGrantsError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setGrantsLoading(false);
        }
    }, [session.idToken]);

    const reloadGrants = useCallback(async () => {
        setGrantsLoading(true);
        setGrantsError(undefined);
        setTwingateDown(false);
        try {
            if (me?.role === 'admin') {
                await syncGrants(session.idToken);
            }
            const response = await fetchGrants(session.idToken);
            setGrants(response);
        } catch (err) {
            if (err instanceof SessionExpiredError) {
                return;
            }
            if (err instanceof TwingateUnreachableError) {
                setTwingateDown(true);
            } else {
                setGrantsError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setGrantsLoading(false);
        }
    }, [session.idToken, me]);

    // Wait for /me to land, then do exactly one initial fetch:
    //   - admin → full sync (POST /grants/sync + GET) so the snapshot is fresh
    //     on every login. The page-head shows "Syncing…" for the duration.
    //   - non-admin → snapshot read only (sync route is 403 for them).
    // A ref guards against the effect re-firing when `me` / callbacks change.
    const initialGrantsLoaded = useRef(false);
    useEffect(() => {
        if (initialGrantsLoaded.current) {
            return;
        }
        if (!me && !meError) {
            return;
        }
        initialGrantsLoaded.current = true;
        if (me?.role === 'admin') {
            reloadGrants();
        } else {
            loadGrants();
        }
    }, [me, meError, reloadGrants, loadGrants]);

    const loadRequests = useCallback(
        async (since: number, until: number, preset?: Range) => {
            setRequests({loading: true, twingateDown: false, since, until, preset});
            try {
                const response = await fetchRequests(session.idToken, {since, until});
                setRequests({
                    loading: false,
                    twingateDown: false,
                    since,
                    until,
                    preset,
                    data: response.requests,
                    accessRequests: response.accessRequests ?? [],
                });
            } catch (err) {
                if (err instanceof SessionExpiredError) {
                    setRequests({loading: false, twingateDown: false, since, until, preset});
                    return;
                }
                if (err instanceof TwingateUnreachableError) {
                    setRequests({loading: false, twingateDown: true, since, until, preset});
                } else {
                    setRequests({
                        loading: false,
                        twingateDown: false,
                        since,
                        until,
                        preset,
                        error: err instanceof Error ? err.message : String(err),
                    });
                }
            }
        },
        [session.idToken]
    );

    // First Requests-page visit triggers an initial fetch; subsequent visits
    // skip if the cache is still good. Only admins see the History tab, so
    // skip the expensive CloudWatch Insights query for everyone else.
    useEffect(() => {
        if (page !== 'requests' || me?.role !== 'admin') {
            return;
        }
        if (requests.data || requests.loading || requests.error || requests.twingateDown) {
            return;
        }
        loadRequests(requests.since, requests.until, requests.preset);
    }, [page, requests, loadRequests, me]);

    const loadUsers = useCallback(async () => {
        setUsersLoading(true);
        setUsersError(undefined);
        setUsersTwingateDown(false);
        try {
            const rows = await fetchUsers(session.idToken);
            setUsers(rows);
        } catch (err) {
            if (err instanceof SessionExpiredError) {
                return;
            }
            if (err instanceof TwingateUnreachableError) {
                setUsersTwingateDown(true);
            } else {
                setUsersError(err instanceof Error ? err.message : String(err));
            }
        } finally {
            setUsersLoading(false);
        }
    }, [session.idToken]);

    // Auto-load once we know the user is admin. Non-admins never have a Users
    // page, so we skip the fetch (it would 403 anyway).
    useEffect(() => {
        if (me?.role !== 'admin') {
            return;
        }
        if (users || usersLoading || usersError || usersTwingateDown) {
            return;
        }
        loadUsers();
    }, [me, users, usersLoading, usersError, usersTwingateDown, loadUsers]);

    const loadClaudeRuns = useCallback(
        async (since: number, until: number, preset?: RunsRange) => {
            setClaudeRuns({loading: true, twingateDown: false, since, until, preset});
            // Session stats come from Logs Insights in INFRA (seconds): fetched alongside, merged when they
            // land, and never allowed to fail the run list. A reply for an older window is dropped.
            const sameWindow = (s: ClaudeRunsPageState) => s.since === since && s.until === until;
            fetchClaudeRunStats(session.idToken, {since, until})
                .then(r => setClaudeRuns(s => (sameWindow(s) ? {...s, stats: r.stats, statsError: undefined} : s)))
                .catch((err: unknown) => {
                    if (err instanceof SessionExpiredError) {
                        return;
                    }
                    const statsError = err instanceof Error ? err.message : String(err);
                    setClaudeRuns(s => (sameWindow(s) ? {...s, statsError} : s));
                });
            try {
                const response = await fetchClaudeRuns(session.idToken, {since, until});
                setClaudeRuns(s => ({
                    loading: false,
                    twingateDown: false,
                    since,
                    until,
                    preset,
                    runs: response.runs,
                    stats: sameWindow(s) ? s.stats : undefined,
                    statsError: sameWindow(s) ? s.statsError : undefined,
                }));
            } catch (err) {
                if (err instanceof SessionExpiredError) {
                    setClaudeRuns({loading: false, twingateDown: false, since, until, preset});
                    return;
                }
                if (err instanceof TwingateUnreachableError) {
                    setClaudeRuns({loading: false, twingateDown: true, since, until, preset});
                } else {
                    setClaudeRuns({
                        loading: false,
                        twingateDown: false,
                        since,
                        until,
                        preset,
                        error: err instanceof Error ? err.message : String(err),
                    });
                }
            }
        },
        [session.idToken]
    );

    // First visit to the Claude runs page triggers the fetch; later visits reuse
    // the cached window. Admin-only route, so skip it for everyone else.
    useEffect(() => {
        if (page !== 'claude-runs' || me?.role !== 'admin') {
            return;
        }
        if (claudeRuns.runs || claudeRuns.loading || claudeRuns.error || claudeRuns.twingateDown) {
            return;
        }
        loadClaudeRuns(claudeRuns.since, claudeRuns.until, claudeRuns.preset);
    }, [page, claudeRuns, loadClaudeRuns, me]);

    // Load the tenant catalog once we're authenticated. Best-effort: on any
    // failure we leave it empty and the dialog falls back to grants-derived
    // tenants (the prior behavior), so this never blocks granting access.
    useEffect(() => {
        if (!me || tenantCatalog.length > 0) {
            return;
        }
        let cancelled = false;
        fetchTenants(session.idToken)
            .then(res => {
                if (!cancelled) {
                    setTenantCatalog(res.tenants);
                }
            })
            .catch(() => {
                /* non-fatal — dialog falls back to grants-derived tenants */
            });
        return () => {
            cancelled = true;
        };
    }, [me, session.idToken, tenantCatalog.length]);

    // Surgical update for in-place edits on the Users page — saves an /users
    // PATCH result without round-tripping the whole list.
    const replaceUser = useCallback((updated: UserProfile) => {
        setUsers(prev => (prev ?? []).map(u => (u.email === updated.email ? updated : u)));
    }, []);

    // Effective me — falls back to the safe default if /me hasn't responded yet
    // or errored out. The UI flickers admin nav into view only after /me lands,
    // which is the right behavior (don't show admin affordances we can't verify).
    const effectiveMe: Me = me ?? {email: session.email, role: 'user', group: 'needs-approval'};

    return (
        <div className="shell">
            <Sidebar
                active={page}
                onNavigate={navigate}
                email={effectiveMe.email}
                role={effectiveMe.role}
                onSignOut={onSignOut}
                onRequestAccess={() => setNewRequestOpen(true)}
            />
            <div className="shell-main">
                {meError && (
                    <div className="page" style={{padding: 16}}>
                        <div className="empty">
                            <h3>Couldn&apos;t load profile</h3>
                            <div>{meError}</div>
                        </div>
                    </div>
                )}
                {!meError && page === 'grants' && effectiveMe.role === 'admin' && (
                    <GrantsPage
                        session={session}
                        data={grants}
                        loading={grantsLoading}
                        error={grantsError}
                        twingateDown={twingateDown}
                        onReload={reloadGrants}
                    />
                )}
                {!meError && page === 'grants' && effectiveMe.role !== 'admin' && (
                    <MyGrantsPage
                        email={effectiveMe.email}
                        data={grants}
                        loading={grantsLoading}
                        error={grantsError}
                        twingateDown={twingateDown}
                        onReload={reloadGrants}
                    />
                )}
                {!meError && page === 'requests' && (
                    <RequestsPage
                        session={session}
                        me={effectiveMe}
                        grants={grants?.grants ?? []}
                        state={requests}
                        onLoad={loadRequests}
                    />
                )}
                {!meError && page === 'users' && effectiveMe.role === 'admin' && (
                    <UsersPage
                        session={session}
                        users={users}
                        loading={usersLoading}
                        error={usersError}
                        twingateDown={usersTwingateDown}
                        onReload={loadUsers}
                        onUserUpdated={replaceUser}
                    />
                )}
                {!meError && page === 'claude-runs' && effectiveMe.role === 'admin' && (
                    <ClaudeRunsPage state={claudeRuns} onLoad={loadClaudeRuns} />
                )}
            </div>

            <NewRequestDialog
                open={newRequestOpen}
                onClose={() => setNewRequestOpen(false)}
                session={session}
                me={effectiveMe}
                grants={grants?.grants ?? []}
                tenantCatalog={tenantCatalog}
                users={users}
                prefill={requestPrefill}
            />
        </div>
    );
}

function loadSession(): AuthSession | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw) as AuthSession;
        if (parsed.expiresAt < Date.now()) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch {
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
}

function decodeJwtPayload(token: string): {email?: string; exp?: number} {
    const parts = token.split('.');
    if (parts.length !== 3) {
        return {};
    }
    try {
        const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(json) as {email?: string; exp?: number};
    } catch {
        return {};
    }
}
